<template>
  <v-dialog
    :value="value"
    @input="onDialogInput"
    max-width="860"
    scrollable
    content-class="linked-rows-dialog-wrapper">
    <v-card class="linked-rows-dialog">
      <!-- Header -->
      <div class="lr-header">
        <div class="lr-header-content">
          <div class="lr-header-icon">
            <v-icon size="22" color="white">mdi-table-search</v-icon>
          </div>
          <div class="lr-header-text">
            <h2 class="lr-header-title">Linked records</h2>
            <p class="lr-header-subtitle">{{ headerSubtitle }}</p>
          </div>
        </div>
        <v-btn
          icon
          small
          width="44" height="44"
          class="lr-close-btn"
          @click="closeDialog">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="lr-body">
        <p class="text-caption mb-4">Linked rows available to this Facility are shown here. Shared Facility Group registers include all linked rows; Separated registers include this Facility's linked rows.</p>
        <!-- ============ LOADING ============ -->
        <div v-if="isLoading" class="lr-state">
          <v-progress-circular indeterminate color="primary" size="32" width="3" />
          <span class="lr-state-title">Loading linked records...</span>
        </div>

        <!-- ============ ERROR ============ -->
        <div v-else-if="loadError" class="lr-notice lr-notice--error">
          <v-icon size="18" color="error">mdi-alert-circle-outline</v-icon>
          <div class="lr-notice-text">
            <span class="lr-notice-title">Could not load linked records</span>
            <span class="lr-notice-body">{{ loadError }}</span>
          </div>
        </div>

        <!-- ============ EMPTY ============ -->
        <!-- An empty admitted member view does not prove that the tag has no links elsewhere. -->
        <div v-else-if="isEmpty" class="lr-state">
          <v-icon size="34" color="grey lighten-1">mdi-table-off</v-icon>
          <span class="lr-state-title">No linked records available to this Facility</span>
          <span class="lr-state-body">
            No linked rows are available in the current Facility view.
          </span>
        </div>

        <!-- ============ RESULTS ============ -->
        <template v-else-if="result">
          <div class="lr-scope">
            <div class="lr-scope-item">
              <span class="lr-scope-value">{{ totalRows }}</span>
              <span class="lr-scope-label">{{ totalRows === 1 ? 'row' : 'rows' }}</span>
            </div>
            <div class="lr-scope-item">
              <span class="lr-scope-value">{{ registers.length }}</span>
              <span class="lr-scope-label">{{ registers.length === 1 ? 'register' : 'registers' }}</span>
            </div>
          </div>

          <div v-if="isTruncated" class="lr-notice">
            <v-icon size="18" class="lr-notice-icon">mdi-information-outline</v-icon>
            <div class="lr-notice-text">
              <span class="lr-notice-title">Showing first {{ rowLimit }} rows</span>
              <span class="lr-notice-body">
                This tag is linked to more rows than that. The counts below describe what is shown.
              </span>
            </div>
          </div>

          <section
            v-for="register in registers"
            :key="register.registerId"
            class="lr-group">
            <div class="lr-group-header">
              <span class="lr-group-name">{{ register.registerName }}</span>
              <span class="lr-group-count">{{ rowCountLabel(register) }}</span>
            </div>

            <v-simple-table dense class="lr-table">
              <thead>
                <tr>
                  <th class="lr-th">Identity</th>
                  <th class="lr-th">Tag value</th>
                  <th class="lr-th">Resolved from</th>
                  <th class="lr-th lr-th--link">Link</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in register.rows" :key="rowKey(register, row)">
                  <td class="lr-td lr-td--identity">{{ row.identityValue }}</td>
                  <td class="lr-td">{{ row.tagValue }}</td>
                  <td class="lr-td">
                    <!-- Legacy rows carry no source cell at all: the junction table stores only the
                         pair. Blank rather than a placeholder, so the gap reads as "not recorded". -->
                    <template v-if="row.sourceColumn">
                      <span class="lr-source-column">{{ row.sourceColumn }}</span>
                      <span v-if="row.sourceValue" class="lr-source-value">{{ row.sourceValue }}</span>
                    </template>
                  </td>
                  <td class="lr-td">
                    <span class="lr-origin" :title="originTitle(row)">
                      <v-icon size="13" class="lr-origin-icon">{{ originIcon(row) }}</v-icon>
                      <span class="lr-origin-label">{{ originLabel(row) }}</span>
                      <span v-if="row.entityType" class="lr-origin-type">{{ row.entityType }}</span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>
        </template>
      </v-card-text>

      <!-- Footer -->
      <div class="lr-footer">
        <v-btn
          text
          class="lr-cancel-btn"
          min-height="44"
          @click="closeDialog">
          Close
        </v-btn>
        <v-btn
          v-if="loadError"
          text
          class="lr-cancel-btn"
          :loading="isLoading"
          min-height="44"
          @click="loadLinkedRows">
          <v-icon left size="18">mdi-refresh</v-icon>
          Try again
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import registerService from '../../services/registerService';
  import { formatDate } from '@/utils';
  import { log as debugLog, error as debugError } from '@/utils/debugLogger';

  // Which of the two register-to-tag link tables a row reached the tag through (AVA-493).
  //
  // AUTHORITY: aiva.api `Controllers/Registers/ViewModels/LinkedRegisterRowsVM.cs`
  // (`RegisterRowLinkOrigins`). The value names the TABLE, not the intent — the POI import
  // resolver writes RegisterEntityLink rows too, so `Materialization` does NOT mean "came from a
  // materialize run". The row's `entityType` is what actually discriminates provenance, which is
  // why it is shown next to the origin rather than translated into one.
  const ROW_LINK_ORIGIN = Object.freeze({
    // RegisterEntityLinks: typed, facility-guarded, carries the cell it was resolved from.
    Materialization: 'materialization',
    // The legacy RegisterRowTags junction: a bare (row, tag) pair and nothing else.
    Legacy: 'legacy',
  });

  const ORIGIN_PRESENTATION = Object.freeze({
    [ROW_LINK_ORIGIN.Materialization]: Object.freeze({
      label: 'Relationship',
      icon: 'mdi-link-variant',
      title: 'Relationship link — carries the register cell it was resolved from.',
    }),
    [ROW_LINK_ORIGIN.Legacy]: Object.freeze({
      label: 'Classic link',
      icon: 'mdi-history',
      title: 'Classic link — the older row-to-tag junction, which records nothing beyond the pair.',
    }),
  });

  export default {
    name: 'LinkedRegisterRowsDialog',
    props: {
      facilityId: { type: String, default: null },
      value: {
        type: Boolean,
        default: false,
      },
      tagId: {
        type: String,
        default: null,
      },
      tagName: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        isLoading: false,
        loadError: null,
        result: null,
        loadRequestId: 0,
      };
    },
    computed: {
      linkedRecordsSessionKey() {
        return JSON.stringify([
          this.$store?.getters?.['ACCOUNT/GET_COMPANY']?.id || '',
          this.$store?.getters?.['ACCOUNT/GET_USER']?.id || '',
        ]);
      },
      registers() {
        return this.result?.registers || [];
      },
      totalRows() {
        return this.result?.totalRows || 0;
      },
      rowLimit() {
        return this.result?.rowLimit || 0;
      },
      isTruncated() {
        return this.result?.truncated === true;
      },
      isEmpty() {
        return Boolean(this.result) && this.registers.length === 0;
      },
      headerSubtitle() {
        return this.tagName || this.result?.tagName || 'Tag';
      },
    },
    watch: {
      linkedRecordsSessionKey() {
        if (this.value) {
          this.resetState();
          this.loadLinkedRows();
        }
      },
      facilityId() {
        if (this.value) {
          this.resetState();
          this.loadLinkedRows();
        }
      },
      // The parent sets the tag before it opens the dialog, so the open transition is the only
      // fetch trigger — watching tagId as well would double-fetch on that same tick.
      value: {
        immediate: true,
        handler(isOpen) {
          if (!isOpen) {
            this.loadRequestId += 1;
            return;
          }
          this.resetState();
          this.loadLinkedRows();
        },
      },
    },
    methods: {
      resetState() {
        this.isLoading = false;
        this.loadError = null;
        this.result = null;
      },
      onDialogInput(isOpen) {
        this.$emit('input', isOpen);
      },
      closeDialog() {
        this.$emit('input', false);
      },
      // The row id is unique per register, but a row can hold two links to the same tag under
      // different entity types, so the key carries the type as well.
      rowKey(register, row) {
        return `${register.registerId}-${row.rowId}-${row.entityType || row.linkOrigin}`;
      },
      // `rowCount` is counted after the response-wide cap, so it describes what is shown here and
      // not what the register holds whenever the cap actually dropped rows.
      rowCountLabel(register) {
        const count = register.rowCount || 0;
        const noun = count === 1 ? 'row' : 'rows';
        return this.isTruncated ? `${count} ${noun} shown` : `${count} ${noun}`;
      },
      originPresentation(row) {
        return ORIGIN_PRESENTATION[row.linkOrigin] || null;
      },
      originLabel(row) {
        return this.originPresentation(row)?.label || row.linkOrigin || '';
      },
      originIcon(row) {
        return this.originPresentation(row)?.icon || 'mdi-link-variant';
      },
      // The entity type and the resolve timestamp are REL-only detail: informative on hover, not
      // worth a column of blanks for every legacy row.
      originTitle(row) {
        const lines = [this.originPresentation(row)?.title || ''];
        if (row.entityType) lines.push(`Entity type: ${row.entityType}`);
        if (row.resolvedAt) lines.push(`Linked ${formatDate(row.resolvedAt, 'YYYY-MM-DD HH:mm')}`);
        return lines.filter(Boolean).join('\n');
      },
      extractErrorMessage(err) {
        const data = err?.response?.data;
        if (typeof data === 'string' && data.trim()) return data;
        return data?.message || err?.message || 'Failed to load the rows linked to this tag.';
      },
      async loadLinkedRows() {
        const requestId = ++this.loadRequestId;
        this.isLoading = false;
        this.result = null;
        if (!this.tagId) {
          this.loadError = 'No tag was supplied.';
          return;
        }
        if (!this.facilityId || !this.facilityId.trim()) {
          this.loadError = 'Open the tag register from a Facility to view its linked records.';
          return;
        }
        const tagId = this.tagId;
        const facilityId = this.facilityId;
        const sessionKey = this.linkedRecordsSessionKey;
        this.isLoading = true;
        this.loadError = null;

        debugLog('Linked register rows fetch starting', { tagId: this.tagId, tagName: this.tagName });

        try {
          const result = await registerService.getTagLinkedRegisterRows(tagId, { facilityId, viewContext: 'member' });
          if (requestId !== this.loadRequestId || tagId !== this.tagId || facilityId !== this.facilityId || sessionKey !== this.linkedRecordsSessionKey || !this.value) return;

          // A payload without the registers array is not an empty result — it is a response we do
          // not understand, and showing the facility-scoped empty copy for it would be a false
          // claim about the tag.
          if (!result || !Array.isArray(result.registers)) {
            throw new Error('The server returned an unexpected response.');
          }

          this.result = result;

          debugLog('Linked register rows loaded', {
            tagId: this.tagId,
            registerCount: result.registers.length,
            totalRows: result.totalRows,
            truncated: result.truncated,
            rowLimit: result.rowLimit,
          });
        } catch (err) {
          if (requestId !== this.loadRequestId || tagId !== this.tagId || facilityId !== this.facilityId || sessionKey !== this.linkedRecordsSessionKey || !this.value) return;
          this.loadError = this.extractErrorMessage(err);
          debugError('Linked register rows fetch failed', {
            tagId: this.tagId,
            message: this.loadError,
          });
        } finally {
          if (requestId === this.loadRequestId) this.isLoading = false;
        }
      },
    },
  };
</script>

<style scoped>
  /* ============================================
     LINKED RECORDS DIALOG
     Flat industrial surfaces: link provenance is carried by icons and text, never by tinted rows.
     ============================================ */

  .linked-rows-dialog {
    --lr-bg-surface: var(--v-bg2-base);
    --lr-bg-elevated: var(--v-bg3-base);
    --lr-text-primary: var(--v-text-base);
    --lr-text-secondary: var(--v-text_secondary-base);
    --lr-border-color: var(--ds-border-default);
    --lr-header-bg: var(--v-primary-base);
    --lr-chrome-bg: #f8f9fa;

    border-radius: var(--ds-radius-lg);
    overflow: hidden;
    background: var(--lr-bg-surface);
    box-shadow: var(--ds-shadow-3);
  }

  .theme--dark .linked-rows-dialog {
    --lr-header-bg: #3d5a3a;
    --lr-chrome-bg: #252525;
  }

  /* Header */
  .lr-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ds-space-md) var(--ds-space-lg);
    background: var(--lr-header-bg);
  }

  .lr-header-content {
    display: flex;
    align-items: center;
    gap: var(--ds-space-sm);
  }

  .lr-header-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--ds-radius-md);
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lr-header-text {
    display: flex;
    flex-direction: column;
  }

  .lr-header-title {
    font-size: var(--ds-font-subtitle);
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }

  .lr-header-subtitle {
    font-size: var(--ds-font-caption);
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    word-break: break-word;
  }

  .lr-close-btn {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  .lr-close-btn:hover {
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.15) !important;
  }

  /* Body */
  .lr-body {
    padding: var(--ds-space-lg) !important;
    background: var(--lr-bg-surface);
  }

  /* Loading / empty states */
  .lr-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ds-space-sm);
    padding: var(--ds-space-xl) var(--ds-space-md);
    text-align: center;
  }

  .lr-state-title {
    font-size: var(--ds-font-body-sm);
    font-weight: 600;
    color: var(--lr-text-primary);
  }

  .lr-state-body {
    font-size: var(--ds-font-caption);
    color: var(--lr-text-secondary);
    max-width: 420px;
  }

  /* Scope strip */
  .lr-scope {
    display: flex;
    align-items: center;
    gap: var(--ds-space-lg);
    padding: var(--ds-space-md);
    background: var(--lr-bg-elevated);
    border: 1px solid var(--lr-border-color);
    border-radius: var(--ds-radius-md);
  }

  .lr-scope-item {
    display: flex;
    flex-direction: column;
  }

  .lr-scope-value {
    font-size: var(--ds-font-subtitle);
    font-weight: 700;
    color: var(--lr-text-primary);
    line-height: 1.2;
  }

  .lr-scope-label {
    font-size: var(--ds-font-micro);
    color: var(--lr-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Notices */
  .lr-notice {
    display: flex;
    align-items: flex-start;
    gap: var(--ds-space-sm);
    padding: var(--ds-space-md);
    margin-top: var(--ds-space-md);
    background: var(--lr-bg-elevated);
    border: 1px solid var(--lr-border-color);
    border-radius: var(--ds-radius-md);
  }

  .lr-notice-icon {
    color: var(--lr-text-secondary) !important;
  }

  .lr-notice-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .lr-notice-title {
    font-size: var(--ds-font-body-sm);
    font-weight: 600;
    color: var(--lr-text-primary);
  }

  .lr-notice-body {
    font-size: var(--ds-font-caption);
    color: var(--lr-text-secondary);
    word-break: break-word;
  }

  .lr-notice--error {
    margin-top: 0;
  }

  .lr-notice--error .lr-notice-title {
    color: var(--v-error-base);
  }

  /* Register groups */
  .lr-group {
    margin-top: var(--ds-space-lg);
  }

  .lr-group-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ds-space-sm);
    padding-bottom: var(--ds-space-xs);
    border-bottom: 1px solid var(--lr-border-color);
  }

  .lr-group-name {
    font-size: var(--ds-font-body-sm);
    font-weight: 600;
    color: var(--lr-text-primary);
    word-break: break-word;
  }

  .lr-group-count {
    flex-shrink: 0;
    font-size: var(--ds-font-micro);
    color: var(--lr-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .lr-table {
    background: transparent !important;
  }

  .lr-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
  }

  .lr-th {
    font-size: var(--ds-font-micro) !important;
    font-weight: 600;
    color: var(--lr-text-secondary) !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .lr-th--link {
    width: 1%;
  }

  .lr-td {
    font-size: var(--ds-font-caption) !important;
    color: var(--lr-text-primary);
    vertical-align: top;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  .lr-td--identity {
    font-weight: 600;
  }

  .lr-source-column {
    display: block;
    color: var(--lr-text-secondary);
  }

  .lr-source-value {
    display: block;
    word-break: break-word;
  }

  /* Link origin */
  .lr-origin {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    cursor: help;
  }

  .lr-origin-icon {
    color: var(--lr-text-secondary) !important;
  }

  .lr-origin-label {
    color: var(--lr-text-secondary);
  }

  .lr-origin-type {
    color: var(--lr-text-secondary);
    opacity: 0.75;
  }

  .lr-origin-type::before {
    content: '·';
    margin-right: 4px;
  }

  /* Footer */
  .lr-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ds-space-md) var(--ds-space-lg);
    background: var(--lr-chrome-bg);
    border-top: 1px solid var(--lr-border-color);
  }

  .lr-cancel-btn {
    color: var(--lr-text-secondary) !important;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }
</style>
